/**
 * Core Web Vitals API
 */

import { useQuery } from '@tanstack/react-query';
import { customerApi } from './customerApi';
import { useSiteStore } from '@/store/site.store';

interface MetricScore {
  score?: number;
  rating: 'GOOD' | 'NEEDS_IMPROVEMENT' | 'POOR' | 'UNKNOWN';
}

interface DeviceMetrics {
  overallScore?: number;
  lcp: MetricScore;
  fid: MetricScore;
  cls: MetricScore;
  ttfb: MetricScore;
  distribution: {
    fast: number;
    average: number;
    slow: number;
  };
}

export interface UrlCwvData {
  url: string;
  measuredAt?: string;
  performanceScoreMobile?: number;
  performanceScoreDesktop?: number;
  performanceCategoryMobile?: string;
  performanceCategoryDesktop?: string;
  lcpMobile?: number;
  lcpDesktop?: number;
  clsMobile?: number;
  clsDesktop?: number;
  ttfbMobile?: number;
  ttfbDesktop?: number;
  fidMobile?: number;
  fidDesktop?: number;
}

export interface CoreWebVitalsResponse {
  snapshotDate?: string;
  mobile: DeviceMetrics;
  desktop: DeviceMetrics;
  summary: {
    totalUrls: number;
    lastUpdated: string;
  };
  urls: UrlCwvData[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useCoreWebVitals() {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = selectedSite?.id;

  return useQuery({
    queryKey: ['coreWebVitals', siteId],
    queryFn: async () => {
      if (!siteId) {
        throw new Error('No site selected');
      }
      const { data } = await customerApi.get<ApiResponse<CoreWebVitalsResponse>>(`/webtools/sites/${siteId}/core-web-vitals`);
      return data.data; // Extract inner data from { success, data } wrapper
    },
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
